import { Tab } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { StatusValue } from "components/Table/StatusValue";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import "react-loading-skeleton/dist/skeleton.css";
import { useStyles } from "./OLTabs.styles";
export interface Item {
  id: string;
  name: string;
  isError?: boolean;
  isInactive?: boolean;
}

interface TabsProps<T> {
  items: T[] | Item[];
  value: string | false;
  onChange: (e: React.SyntheticEvent, activeTab: T) => void;
  "data-test-id"?: string;
  className?: string;
  maxLabelLength?: number;
}

export function OLTabs<T>({
  items,
  value,
  onChange,
  className = "",
  maxLabelLength,
}: TabsProps<T>) {
  const classes = useStyles();
  const { message } = useMessage();

  const isObject = (obj: any): obj is object => {
    return typeof obj === "object" && obj !== null;
  };

  return (
    <div className={classes.displayFlex}>
      <Tabs
        allowScrollButtonsMobile
        className={`${classes.tabsContainer} ${classes.scrollButton} ${className}`}
        onChange={onChange}
        scrollButtons
        value={value}
        variant="scrollable"
        slotProps={{
          startScrollButtonIcon: () => ({
            "data-test-id": "keyboard-arrow-left-icon",
          }),
          endScrollButtonIcon: () => ({
            "data-test-id": "keyboard-arrow-right-icon",
          }),
        }}
      >
        {items.map((tab, index: number) => {
          const isLabelTruncated = typeof maxLabelLength === "number";
          const label = message({
            id: isObject(tab) ? (tab as Item).name : String(tab),
          });
          const isLabelToLong =
            isLabelTruncated && label.length > maxLabelLength;
          const isInactive = (tab as Item)?.isInactive;
          return (
            <Tab
              key={index}
              value={isObject(tab) ? (tab as Item).id : String(tab)}
              label={
                <Tooltip text={isLabelToLong ? label : ""}>
                  <div className={classes.labelWrapper}>
                    {isLabelToLong
                      ? `${label.substring(0, maxLabelLength)}...`
                      : label}
                    {isInactive && <StatusValue status={false} />}
                  </div>
                </Tooltip>
              }
              className={classes.tab}
              data-test-id={isObject(tab) ? `${label}-tab` : `${tab}-tab`}
              icon={
                (tab as Item).isError ? (
                  <AlertTriangleIcon className={classes.error} />
                ) : undefined
              }
              iconPosition="start"
              data-sentry-unmask
            />
          );
        })}
      </Tabs>
    </div>
  );
}
